import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import {
  ILinks,
  IMeta,
  IRequestOptions,
} from './../../../app/@store/request-options.interface';
import { User } from './../../../app/@shared/models/user.model';
import { HttpErrorResponse } from '@angular/common/http';
import { createReducer, on } from '@ngrx/store';
import * as UserActions from './user.actions';
import * as _ from 'lodash';
import { ColumnSortDirection } from '@gridscale/ingrid/components/data-table';
import { UsersResponse } from './../../../app/@shared/models/users_response.model';

export const userFeatureKey = 'user';

export const adapter: EntityAdapter<Partial<User>> = createEntityAdapter<
  Partial<User>
>({
  selectId: (a => a.object_uuid || ''), // define "primary id"
  sortComparer: false,
});

export const listAdapter: EntityAdapter<Partial<UsersResponse['users'][0]>> =
  createEntityAdapter<Partial<UsersResponse['users'][0]>>({
    selectId: (a => a.object_uuid || ''), // define "primary id"
    sortComparer: false,
  });

const listAdapterInitialState = listAdapter.getInitialState({
  listConfig: {
    limit: 50,
    sort: '+email',
    //fields: ['object_uuid', 'name', 'payment_enabled', 'locked', 'disconnected', 'change_time', 'create_time', 'feature_flags', 'prices', 'last_billing_time', 'resource_limits', 'relations', 'favorite'],
    page: 0,
  },
  loaded: false,
  loading: false,
  loadingNext: false,
  search: '',
}) as ListState;

export interface ListState
  extends EntityState<Partial<UsersResponse['users'][0]>> {
  listConfig: IRequestOptions;
  loaded: boolean;
  loading: boolean;
  loadingNext: boolean;
  meta?: IMeta;
  links?: ILinks<User>;
  error?: Error | HttpErrorResponse | undefined;
}

export interface State extends EntityState<Partial<User>> {
  loading: { [key: string]: boolean };
  loaded: { [key: string]: boolean };
  patching: { [key: string]: boolean };
  deleting: { [key: string]: boolean | undefined };
  error?: { [key: string]: Error | HttpErrorResponse | undefined };
  list: ListState;
  total: number;
  autocomplete: Partial<UsersResponse['users']> | undefined;
  autocomplete_loading: boolean;
}

export const initialState: State = adapter.getInitialState({
  loading: {},
  loaded: {},
  patching: {},
  deleting: {},
  list: listAdapterInitialState,
  total: 0,
  autocomplete: [],
  autocomplete_loading: false,
});

export const reducer = createReducer(
  initialState,

  on(UserActions.setListConfig, (state, action) => ({
    ...state,

    list: listAdapter.removeAll({
      ...state.list,
      loaded: false,
      listConfig: {
        ...action.config,
      },
    }),
  })),

  on(UserActions.loadUsers, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: true,
    },
  })),

  on(UserActions.loadUsersSuccess, (state: State, action) => ({
    ...state,

    list: listAdapter.setAll(action.data, {
      ...state.list,
      meta: action.meta,
      loading: false,
      loaded: true,
      error: undefined,
    }),
  })),

  on(UserActions.loadUsersFailure, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      entities: {},
      loading: false,
      loaded: true,
      error: action.error,
    },
  })),

  on(UserActions.loadUsersTotalSuccess, (state: State, action) => ({
    ...state,
    total: action.total,
  })),

  on(UserActions.loadUsersNext, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: true,
      loadingNext: true,
    },
  })),

  on(UserActions.loadUsersNextSuccess, (state: State, action) => {
    return {
      ...state,

      list: listAdapter.upsertMany(action.data, {
        ...state.list,
        meta: action.meta,
        loading: false,
        loadingNext: false,
        loaded: true,
        error: undefined,
      }),
    };
  }),

  on(UserActions.loadUsersNextFailure, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: false,
      loadingNext: false,
      loaded: true,
      error: action.error,
    },
  })),

  on(UserActions.userTableChangeSort, (state, action) => {
    const sort =
      (action.newSort.direction === ColumnSortDirection.ASC ? '+' : '-') +
      action.newSort.columnId;

    return {
      ...state,
      list: {
        ...state.list,
        listConfig: {
          ...state.list.listConfig,
          sort,
        },
      },
    };
  }),

  on(
    UserActions.patchUser,
    UserActions.changeUserRole,
    UserActions.patchEmail,
    UserActions.loadUser,
    UserActions.toggleFavorite,
    UserActions.addUserPartnerRole,
    UserActions.patchUserPartnerRole,
    UserActions.removeUserPartnerRole,
    (state, action) => ({
      ...state,

      patching: { ...state.patching, [action.user_uuid]: true },
      error: { ...state.error, [action.user_uuid]: undefined },
    })
  ),
  on(
    UserActions.patchUserSuccess,
    UserActions.changeUserRoleSuccess,
    UserActions.patchEmailSuccess,
    UserActions.loadUserSuccess,
    UserActions.toggleFavoriteSuccess,
    UserActions.addUserPartnerRoleSuccess,
    UserActions.patchUserPartnerRoleSuccess,
    UserActions.removeUserPartnerRoleSuccess,
    (state, action) => ({
      ...state,

      patching: { ...state.patching, [action.user_uuid]: false },
      error: { ...state.error, [action.user_uuid]: undefined },
    })
  ),
  on(
    UserActions.patchUserFailure,
    UserActions.changeUserRoleFailure,
    UserActions.patchEmailFailure,
    UserActions.loadUserFailure,
    UserActions.toggleFavoriteFailure,
    UserActions.addUserPartnerRoleFailure,
    UserActions.patchUserPartnerRoleFailure,
    UserActions.removeUserPartnerRoleFailure,
    (state, action) => ({
      ...state,

      patching: { ...state.patching, [action.user_uuid]: false },
      error: { ...state.error, [action.user_uuid]: action.error },
    })
  ),

  on(UserActions.loadUser, (state, action) => ({
    ...state,

    loading: { ...state.loading, [action.user_uuid]: true },
    loaded: { ...state.loaded, [action.user_uuid]: false },
  })),

  on(UserActions.loadUserSuccess, (state, action) => {
    return adapter.setOne(action.data, {
      ...state,
      error: { ...state.error, [action.user_uuid]: undefined },
      loading: { ...state.loading, [action.user_uuid]: false },
      loaded: { ...state.loaded, [action.user_uuid]: true },
      list: listAdapter.setOne(action.data, {
        ...state.list,
        loaded: true,
        error: undefined,
        loading: false,
        loadingNext: false,
      }),
    });
  }),

  on(UserActions.loadUserFailure, (state, action) => ({
    ...state,

    error: { ...state.error, [action.user_uuid]: action.error },
    loading: { ...state.loading, [action.user_uuid]: false },
    loaded: { ...state.loaded, [action.user_uuid]: true },
  })),

  on(UserActions.patchUserSuccess, (state, action) => {
    return {
      ...state,

      list: listAdapter.updateOne(
        { id: action.user_uuid, changes: { ...action.data } },
        {
          ...state.list,
        }
      ),
    };
  }),

  on(UserActions.deleteUser, (state, action) => ({
    ...state,

    deleting: { ...state.deleting, [action.user_uuid]: true },
    error: { ...state.error, [action.user_uuid]: undefined },
  })),

  on(UserActions.deleteUserSuccess, (state, action) => ({
    ...state,

    deleting: { ...state.deleting, [action.user_uuid]: undefined },
    error: { ...state.error, [action.user_uuid]: undefined },

    list: listAdapter.removeOne(action.user_uuid, {
      ...state.list,

      meta: {
        ..._.get(state, 'list.meta', {limit: 0, page: 0}),
        count: _.get(state, 'list.meta.count', 0) - 1,
        total: _.get(state, 'list.meta.total', 0) - 1
      }
    }),
  })),


  on(UserActions.deleteUserFailure, (state, action) => ({
    ...state,

    deleting: { ...state.deleting, [action.user_uuid]: false },
    error: { ...state.error, [action.user_uuid]: action.error },
  })),

  on(UserActions.setSearch, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      search: action.search,
    },
  })),

  on(UserActions.toggleFavoriteSuccess, (state, action) => {
    return {
      ...state,
      list: {
        ...state.list,
        entities: {
          ...state.list.entities,
          [action.user_uuid]: {
            ...state.list.entities[action.user_uuid],
            favorite: action.favorite,
          },
        },
      },
    };
  }),

  // Autocomplete

  on(UserActions.loadUsersAutocomplete, (state, action) => ({
    ...state,
    autocomplete_loading: true,
  })),
  on(UserActions.loadUsersAutocompleteSuccess, (state, action) => ({
    ...state,
    autocomplete_loading: false,
    autocomplete: action.data,
  })),
  on(UserActions.loadUsersAutocompleteFailure, (state, action) => ({
    ...state,
    autocomplete_loading: false,
  }))
);
